import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import * as carto from '@carto/carto-vl';
import * as mapboxgl from '@carto/mapbox-gl';
import { default as bboxPolygon } from '@turf/bbox-polygon';
import { MapContextService } from 'app/common/map-context.service';
import { center, zoom } from 'app/common/const';
import { Deck } from '@deck.gl/core';
import { environment } from 'environments/environment';

const INITIAL_VIEW_STATE = {
  latitude: environment.center[1],
  longitude: environment.center[0],
  zoom: environment.zoom,
  bearing: 0,
  pitch: 0,
};

@Component({
  selector: 'app-base-map',
  templateUrl: './base-map.component.html',
  styleUrls: ['./base-map.component.scss'],
})
export class BaseMapComponent implements OnInit {
  @ViewChild('map') mapContainer;
  map: any;
  deck: Deck;

  constructor(
    protected mapContext: MapContextService,
    protected ngZone: NgZone
  ) {}

  ngOnInit() {
    mapboxgl.accessToken =
      'pk.eyJ1IjoicGFkYXdhbm5uIiwiYSI6ImNqM2Q0aXQ1YjAwMWYyd3FvZWFtNWhzcWkifQ.RoQyI1QgQHNeztjjCnNIwg';
    this.map = new mapboxgl.Map({
      container: this.mapContainer.nativeElement,
      center: center,
      zoom: zoom,
      pitch: 0,
    })
      .on('move', (e) => {
        this.mapContext.bboxChange.next(e);
      })
      .on('load', (e) => {
        this.mapContext.mapLoaded.next(this.map);
      });

    this.map.dragRotate.disable();
    this.map.touchZoomRotate.disableRotation();

    carto.setDefaultAuth({
      user: environment.options.cartoAccount || 'traffico-geographica',
      apiKey: 'default_public',
    });
    this.mapContext.mapInstance = this.map;
    this.ngZone.runOutsideAngular(() => {
      this.deck = new Deck({
        canvas: 'deck-canvas',
        width: '100%',
        height: '100%',
        getCursor: () => '',
        useDevicePixels: true,
        initialViewState: INITIAL_VIEW_STATE,
        controller: true,
        onViewStateChange: ({ viewState }) => {
          this.deck.setProps({ viewState });
          this.ngZone.run(() => {
            this.mapContext.onViewStateChange.next(viewState);
          });
        },
        onBeforeRender: (_) => {
          if (this.deck) {
            const viewport = this.deck.getViewports()[0];
            this.map.jumpTo({
              center: [viewport.longitude, viewport.latitude],
              zoom: viewport.zoom,
              bearing: viewport.bearing,
              pitch: viewport.pitch,
            });
            // TODO: only redraw when viewport has changed
            this.redrawMapbox();
          }
        },
        onAfterRender: (_) => {
          this.mapContext.onViewRender.next();
          this.getBBOX();
        },
        onClick: (event) => {
          this.mapContext._coordinates = event;
          this.mapContext.currentMapEvent.next(event);
        },
        layers: [],
      });
    });
    (window as any).deckMap = this.deck;

    this.mapContext.deckInstance = this.deck;
    this.mapContext
      .getMapStyle()
      .then((_mapstyle) => this.mapContext.mapInstance.setStyle(_mapstyle));
  }

  // code taken from here: https://github.com/visgl/react-map-gl/blob/ce6f6662ca34f8765cf0f515039e316adb52a957/src/mapbox/mapbox.js#L421
  // Force redraw the map now. Typically resize() and jumpTo() is reflected in the next
  // render cycle, which is managed by Mapbox's animation loop.
  // This removes the synchronization issue caused by requestAnimationFrame.
  redrawMapbox() {
    const map = this.map;
    // map._render will throw error if style does not exist
    // https://github.com/mapbox/mapbox-gl-js/blob/fb9fc316da14e99ff4368f3e4faa3888fb43c513/src/ui/map.js#L1834
    if (map.style) {
      // cancel the scheduled update
      if (map._frame) {
        map._frame.cancel();
        map._frame = null;
      }
      // the order is important - render() may schedule another update
      map._render();
    }
  }

  getBBOX() {
    const bounds = this.map.getBounds(),
      sw = bounds.getSouthWest(),
      ne = bounds.getNorthEast();
    this.mapContext._bbox = [sw.lng, sw.lat, ne.lng, ne.lat];
    return bboxPolygon([sw.lng, sw.lat, ne.lng, ne.lat]);
  }

  protected openPopup(popups) {
    this.ngZone.run(() => this.mapContext.openPopup(popups));
  }

  protected closePopups() {
    this.ngZone.run(() => this.mapContext.closePopup());
  }
}



// WEBPACK FOOTER //
// ./src/app/common/base-map/base-map.component.ts